import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Auth } from "@aws-amplify/auth";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useFormik } from "formik";
import { Button, BUTTON_SIZES } from "../components/Button";
import AuthInput from "components/AuthInput";
import Select from "components/Select";
import { Checkbox } from "components/Checkbox";
import * as Axios from "../providers/axios";
import AuthLayout from "components/AuthLayout";
import { authSubmitButtonStyles, authLinkButtonStyles } from "utils/constants";
import { SignUpRequestValues } from "types";
import PhoneInputSignUp from "components/PhoneInputSignUp";
import { isPhoneValid } from "utils/isPhoneValid";

const pronounSelectOptions = [
  {
    label: "She/Her",
    value: "She/Her"
  },
  {
    label: "He/Him",
    value: "He/Him"
  },
  {
    label: "They/Them",
    value: "They/Them"
  },
  {
    label: "Other",
    value: "Other"
  }
];

const NOT_APPROVED_EMAIL_ERROR = `Pointment is in closed beta, join our waitlist and 
we will notify you about our next opening.`;

export default function RegisterArtist() {
  const navigate = useNavigate();
  const { api } = Axios.useAxios();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loadingApproveEmails, setLoadingApproveEmails] =
    useState<boolean>(true);
  const [approvedEmails, setApprovedEmails] = useState<string[]>([]);
  const [restrictedSignUp, setRestrictedSignUp] = useState(false);

  useEffect(() => {
    const fetchPlatforms = async () => {
      setLoadingApproveEmails(true);
      const { data } = await api.get("/v1/platform");
      setApprovedEmails(data.approved_emails.split(","));
      setRestrictedSignUp(data.feature_flag_restricted_sign_up);
      setLoadingApproveEmails(false);
    };

    fetchPlatforms();
  }, [api]);

  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get("email");
  const firstName = urlParams.get("firstName");
  const lastName = urlParams.get("lastName");
  const phoneNumber = urlParams.get("phoneNumber");

  const { handleSubmit, handleChange, touched, errors, values, setFieldValue } =
    useFormik({
      initialValues: {
        firstName: firstName || "",
        lastName: lastName || "",
        email: email || "",
        password: "",
        password_confirmation: "",
        phone_number: phoneNumber || "+1",
        pronoun: "",
        newsletter: false,
        reminders: false,
        promoReminders: false,
        termsAndConditionsAccepted: false,
        isInvited: !!email
      },
      validationSchema: RegisterSchema,
      validateOnChange: true,
      validateOnMount: false,
      validateOnBlur: false,
      onSubmit: async ({
        firstName,
        lastName,
        email,
        password,
        phone_number,
        pronoun,
        newsletter,
        reminders,
        promoReminders,
        termsAndConditionsAccepted
      }: SignUpRequestValues & {
        isInvited: boolean;
        password_confirmation: string;
      }) => {
        try {
          setSubmitting(true);

          // if sign up is restricted and email is not in approved list
          if (
            restrictedSignUp &&
            !approvedEmails.includes(email.toLowerCase())
          ) {
            errors.email = NOT_APPROVED_EMAIL_ERROR;
            setSubmitting(false);
            return;
          }

          const registeredUser = await Auth.signUp({
            username: email.toLowerCase(),
            password
          });

          await api.post("/v1/users", {
            firstName,
            lastName,
            email: email.toLowerCase(),
            phone: phone_number,
            pronoun,
            newsletter,
            reminders,
            promoReminders,
            termsAndConditionsAccepted,
            sub: registeredUser.userSub
          });

          return navigate(`/verify?email=${email.toLowerCase()}`);
        } catch (err: any) {
          if (err.message.startsWith("Password did not conform with policy:")) {
            errors.password = err.message;
          } else if (
            err.message === "An account with the given email already exists."
          ) {
            errors.email = err.message;
          }
        } finally {
          setSubmitting(false);
        }
      }
    });

  return (
    <>
      <Helmet>
        <title>Pointment | Register</title>
        <meta property="og:title" content="Pointment | Register" />
      </Helmet>
      <AuthLayout title="Artist sign up">
        <form onSubmit={handleSubmit}>
          <AuthInput
            type="text"
            name="firstName"
            label="First name"
            placeholder="First name"
            value={values.firstName}
            onChange={handleChange}
            touched={touched}
            errors={errors}
            autocapitalize="none"
          />
          <AuthInput
            type="text"
            name="lastName"
            label="Last name"
            placeholder="Last name"
            value={values.lastName}
            onChange={handleChange}
            touched={touched}
            errors={errors}
            autocapitalize="none"
          />
          {!email && (
            <>
              <AuthInput
                type="email"
                name="email"
                label="Email"
                placeholder="hello@pointmentapp.com"
                value={values.email}
                onChange={handleChange}
                touched={touched}
                errors={errors}
                autocapitalize="none"
              />
              {restrictedSignUp &&
                errors.email === NOT_APPROVED_EMAIL_ERROR && (
                  <a
                    href="https://pointmentapp.com/pages/join-the-waitlist"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#1C809A] font-['Inter'] text-xs ml-4 mb-3 block"
                  >
                    Join our Waitlist →
                  </a>
                )}
            </>
          )}
          <AuthInput
            type="password"
            name="password"
            label="Password"
            placeholder="Password"
            value={values.password}
            onChange={handleChange}
            touched={touched}
            errors={errors}
          />
          <AuthInput
            type="password"
            name="password_confirmation"
            label="Password Confirmation"
            placeholder="Password Confirmation"
            value={values.password_confirmation}
            onChange={handleChange}
            touched={touched}
            errors={errors}
          />
          <PhoneInputSignUp
            fullWidth
            touched={touched}
            errors={errors}
            value={values.phone_number}
            setFieldValue={setFieldValue}
            cyId="phone-input"
          />
          <Select
            value={values.pronoun}
            onChange={setFieldValue}
            label="Pronoun"
            options={pronounSelectOptions}
            className="mb-3.5"
          />
          {/* <Checkbox
            id="newsletter"
            value={values.newsletter}
            onChange={setFieldValue}
            label="Subscribe to our newsletter"
            className="mb-3"
          /> */}
          <Checkbox
            id="reminders"
            value={values.reminders}
            cyId="reminders-checkbox"
            onChange={setFieldValue}
            label={`I agree to receive automated customer care text messages and 
              emails from Pointment related to my admin account and salon's appointments. 
              Message frequency may vary. Msg & data rates may apply. Reply STOP 
              to cancel or HELP for assistance.`}
            className="mb-3.5"
            checkTopAlign
          />
          <Checkbox
            id="promoReminders"
            value={values.promoReminders}
            cyId="promo-reminders-checkbox"
            onChange={setFieldValue}
            label={`I agree to receive automated promotional text messages and 
              emails from Pointment, including product updates and new offers. 
              Message frequency may vary. Msg & data rates may apply. Reply STOP 
              to cancel or HELP for assistance. Consent is not a condition of purchase.`}
            className="mb-3.5"
            checkTopAlign
          />
          <Checkbox
            id="termsAndConditionsAccepted"
            value={values.termsAndConditionsAccepted}
            cyId="terms-and-conditions-checkbox"
            onChange={setFieldValue}
            errors={errors}
            touched={touched}
            label={
              <div>
                <p className="caption text-xs font-['Inter'] font-normal">
                  I agree to Pointment's{" "}
                  <a
                    className="caption text-[#1C809A] font-['Inter']"
                    href="/toc"
                    target="_blank"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    className="caption text-[#1C809A] font-['Inter']"
                    href="/privacypolicy"
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            }
            className="mb-3.5"
            checkTopAlign
            withValidation={true}
          />
          <div className="flex gap-[16px]">
            {!email && (
              <Link
                className="cursor-pointer w-full"
                to={"/login"}
                tabIndex={-1}
              >
                <Button
                  className={authLinkButtonStyles}
                  text={"Not new?"}
                  size={BUTTON_SIZES.MEDIUM}
                  type="button"
                />
              </Link>
            )}
            <Button
              className={`${authSubmitButtonStyles} ${email ? "!w-full" : ""}`}
              text={"Sign up"}
              type="submit"
              size={BUTTON_SIZES.MEDIUM}
              submitting={submitting}
              disabledOnSubmit
              disabled={loadingApproveEmails}
            />
          </div>
        </form>
      </AuthLayout>
    </>
  );
}

const RegisterSchema = Yup.object().shape({
  firstName: Yup.string().required("This field is required"),
  lastName: Yup.string().required("This field is required"),
  email: Yup.string().email().required("This field is required"),
  termsAndConditionsAccepted: Yup.boolean().oneOf(
    [true],
    "This field is required"
  ),
  password: Yup.string().required("This field is required"),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("This field is required"),
  phone_number: Yup.string().test(
    "phone",
    "Not valid phone number format",
    (value) => {
      return isPhoneValid(value);
    }
  )
});
